import React, { useState, useEffect } from "react";
import { useAction, useQuery } from "wasp/client/operations";
import {
  updateRequestOffer,
  getRequestOfferById,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import {
  ArrowLeft,
  HandHeart,
  DollarSign,
  Tag,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Package,
  Star,
  Gift,
  Mail,
} from "lucide-react";

interface RequestOfferFormData {
  title: string;
  description: string;
  price: string;
  category: "REQUEST" | "OFFER";
  condition: "NEW" | "LIKE_NEW" | "GOOD" | "FAIR" | "POOR";
  phoneNumber: string;
  email: string;
  visibility: "VERIFIED" | "PENDING" | "REJECTED";
}

const REQUEST_OFFER_CATEGORIES = [
  { value: "OFFER", label: "Offer", icon: Gift },
  { value: "REQUEST", label: "Request", icon: HandHeart },
];

const CONDITION_OPTIONS = [
  { value: "NEW", label: "New", description: "Brand new, never used" },
  {
    value: "LIKE_NEW",
    label: "Like New",
    description: "Excellent condition, barely used",
  },
  { value: "GOOD", label: "Good", description: "Good condition, some wear" },
  {
    value: "FAIR",
    label: "Fair",
    description: "Fair condition, noticeable wear",
  },
  {
    value: "POOR",
    label: "Poor",
    description: "Poor condition, significant wear",
  },
];



// Tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function EditRequestOfferPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const updateRequestOfferFn = useAction(updateRequestOffer);

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getRequestOfferById, { itemId: itemId || "" });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RequestOfferFormData>({
    title: "",
    description: "",
    price: "",
    category: "OFFER",
    condition: "GOOD",
    phoneNumber: "",
    email: "",
    visibility: "VERIFIED",
  });

  // Populate form when item data loads
  useEffect(() => {
    if (item) {
      const itemData = item as any;
      setFormData({
        title: itemData.title || "",
        description: itemData.description || "",
        price: itemData.metadata?.price?.toString() || "",
        category: itemData.metadata?.category || "OFFER",
        condition: itemData.metadata?.condition || "GOOD",
        phoneNumber: itemData.metadata?.phoneNumber || "",
        email: itemData.metadata?.email || "",
        visibility: itemData.visibility || "VERIFIED",
      });
    }
  }, [item]);

  const handleChange = (field: keyof RequestOfferFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!itemId) {
      setErrors({ submit: "Item ID is missing" });
      return;
    }

    setIsSubmitting(true);

    try {
      await updateRequestOfferFn({
        itemId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: formData.price ? parseFloat(formData.price) : undefined,
        category: formData.category,
        condition: formData.condition,
        phoneNumber: formData.phoneNumber.trim(),
        email: formData.email.trim(),
      });

      // Navigate back to the request-offer page
      handleGoBack();
    } catch (error) {
      console.error("Error updating request/offer item:", error);
      setErrors({ submit: "Failed to update item. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/request-offer`);
    } else {
      navigate("/request-offer");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You must be logged in to post request/offer items.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Item...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading item...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (error || !item) {
    return (
      <IndividualEditPageLayout title="Item Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Item Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The item you're looking for doesn't exist or you don't have
            permission to edit it.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  // Check if user can edit this item
  if ((item as any).authorId !== user.id) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 mb-4">
            You can only edit your own items.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="request-offer-form"
        disabled={isSubmitting || !formData.title.trim()}
        icon={Save}
      >
        {isSubmitting ? "Updating..." : "Update"}
      </ActionButton>

      <ActionButtonSecondary onClick={handleGoBack} disabled={isSubmitting}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Tips for Better Results" hideOnMobile>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Use clear, descriptive titles that include key details</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>
              Include detailed descriptions of what you need or are offering
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Set a fair price or mention if it's free/negotiable</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>
              Provide accurate contact information for easy communication
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>
              Be responsive to inquiries to increase your chances of success
            </span>
          </div>
        </div>
    </SidebarSection>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Request/Offer Item"
      subtitle={`Location: ${selectedLocation?.name || "No location selected"}`}
      icon={HandHeart}
      actions={actionsContent}
      sidebar={sidebarContent}
    >

      <form id="request-offer-form" onSubmit={handleSubmit}>
        {/* Full-width sections */}
        <div className="space-y-6 mb-6">
          {/* What are you posting? */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              What are you posting?
            </h3>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${errors.title
                ? "border-destructive bg-destructive/10"
                : "border-input"
                }`}
              placeholder="e.g., Looking for a laptop, Offering tutoring services"
              required
            />
            {errors.title && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Description
            </h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${errors.description
                ? "border-destructive bg-destructive/10"
                : "border-input"
                }`}
              placeholder="Provide detailed information about what you're requesting or offering..."
              required
            />
            {errors.description && (
              <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
          </div>
        </div>

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Category and Condition */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    handleChange(
                      "category",
                      e.target.value as "REQUEST" | "OFFER",
                    )
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  {REQUEST_OFFER_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Condition
                </label>
                <select
                  value={formData.condition}
                  onChange={(e) =>
                    handleChange(
                      "condition",
                      e.target.value as
                      | "NEW"
                      | "LIKE_NEW"
                      | "GOOD"
                      | "FAIR"
                      | "POOR",
                    )
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  {CONDITION_OPTIONS.map((condition) => (
                    <option key={condition.value} value={condition.value}>
                      {condition.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center gap-2">
                  <span>Price</span>
                  <Tooltip content="Leave empty if you prefer to negotiate or if it's free">
                    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      handleChange("email", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleChange("phoneNumber", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
